import React, {FunctionComponent} from "react";
import {Card, CardActionArea, CardContent, Chip, Radio, Stack, Typography, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {Product} from "../../services/product-service";
import {formatProductPrice, formatTaxHint} from "../../services/product-format";

type PlanSelectionProps = {
    products: Product[],
    selected?: string,
    onSelect: (productType: string) => void,
};

export const PlanSelection: FunctionComponent<PlanSelectionProps> = ({products, selected, onSelect}) => {
    const {t} = useTranslation();
    const theme = useTheme();

    const plans = products.map(product => ({
        key: product.productType,
        label: t(product.name),
        price: formatProductPrice(t, product),
        tax: formatTaxHint(t, product),
        sub: t(product.description),
        badge: product.productType === 'yearly' ? t('signupBreeder.plan.yearlyBadge') : null,
    }));

    return (
        <Stack width="100%" spacing={2} direction={{xs: 'column', sm: 'row'}}>
            {plans.map((plan) => (
                <Card
                    key={plan.key}
                    variant="outlined"
                    sx={{
                        borderColor: selected === plan.key ? theme.palette.primary.main : 'divider',
                        backgroundColor: selected === plan.key ? theme.palette.action.hover : 'inherit',
                        position: 'relative',
                        flex: 1,
                    }}
                >
                    <CardActionArea onClick={() => onSelect(plan.key)}>
                        <CardContent>
                            <Stack
                                direction={{xs: 'column', sm: 'row'}}
                                alignItems={{xs: 'flex-start', sm: 'center'}}
                                justifyContent="space-between"
                                spacing={1}
                                sx={{mb: 2}}
                            >
                                <Stack direction="row" spacing={0.5}>
                                    <Radio
                                        checked={selected === plan.key}
                                        value={plan.key}
                                        size="small"
                                        sx={{p: 0}}
                                    />
                                    <Typography variant="subtitle1" fontWeight="bold">{plan.label}</Typography>
                                </Stack>
                                {plan.badge && (
                                    <Chip label={plan.badge} color="primary" size="small"/>
                                )}
                            </Stack>
                            <Typography variant="h5" fontWeight="bold">{plan.price}</Typography>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{mb: 2}}>
                                {plan.sub}, {plan.tax}
                            </Typography>
                        </CardContent>
                    </CardActionArea>
                </Card>
            ))}
        </Stack>
    );
};
